import type {BookingInterface} from '@/types/booking';
import type {TemplateInterface} from '@/store/modules/template';
import type {VehicleImageInterface, VehicleInterface} from '@/types/vehicle';
import type {User} from '@/types/user';
import type {TripExtra} from '@/types/trip-extra';
import type {TaxCategory} from '@/types/taxes';
import type {Nullable} from '@/types/utils';
import type {ListingItemType} from '@/constants/listingItemTypes';

export interface DraftListingInterface {
    name: string,
    item_type: ListingItemType,
    item_label: string | null,
    pricing: {
        amount: number,
        price_period: 'Day',
    },
    vehicle: {
        year: number | null,
        make: string | null,
        model: string | null,
        color: string | null,
        plate: string | null,
    },
}

export interface ListingImportInterface {
    description: string,
    make: string,
    model: string,
    name: string,
    price: string,
    seats: string,
    security_deposit: string,
    unit_id: string,
    vin: string,
    year: string,
}

export interface ListingInterface {
    /** What the listing rents out; missing means vehicle. */
    item_type?: ListingItemType;
    /** What the host calls it ("Horse trailer"); null uses the type's label. */
    item_label?: string | null;
    checkout_url?: string;
    loaded?: boolean,
    active: boolean,
    approved: boolean,
    archived_at: Nullable<string>,
    deleted_at?: Nullable<string>,
    cover_photo: Nullable<string>,
    is_custodian: boolean,
    custodian?: User,
    init?: boolean,
    l_id: Nullable<string>,
    unit_id?: Nullable<string>,
    listing_image: string,
    min_age: number,
    name: string,
    checkout_mode: string,
    payment_collection_mode?: 'charge_now' | 'ask_customer' | 'pay_at_pickup',
    matched_getaround_vehicle?: {
        name: string,
    },
    order: number,
    paused: boolean,
    city?: string,
    state?: string
    description?: string,
    tax_category?: TaxCategory | null,
    seasoned_driver: number | null,
    create_mode: 'manual' | 'turo_import' | 'getaround_import' | 'tesla_import' | 'bouncie_import',
    pricing?: ListingPricing,
    /** Custom daily prices from today onwards; only present on the listing detail response. */
    upcoming_daily_prices_count?: number,
    vehicle?: VehicleInterface,
    listing_images: VehicleImageInterface[],
    bookings: BookingInterface[],
    miles_included: number,
    longer_weekend_trip_preferred: boolean,
    miles_extra: number,
    minimum_request_duration: Nullable<string>,
    maximum_request_duration: Nullable<string>,
    padding_after: number | null,
    padding_before: number | null,
    provider_listings: any[] | null,
    slug: string,
    sub_title: string,
    template?: TemplateInterface | null,
    timezone: string,
    title: string,
    extras: TripExtra[],
    use_esign_template: boolean,
    use_secondary_driver: boolean,
    commission_partners: any[] | null,
    distance_allowance_max: number,
    age_requirements?: object,
    available?: {
        available: boolean,
        booking: BookingInterface | null,
    },
    base_price_in_cents?: number | null,
    pricingStructure?: {
        name: string;
        hour: number;
        day: number;
        week: number;
        month: number;
        year: number;
        price_structure_type: string;
        calculation_mode?: 'tiered' | 'repeatable';
        product_group_id: string | null;
        uuid: string;
        archived_at: string | null;
        created_at: string | null;
        updated_at: string;
        pricing_tiles: Array<{
            pricing_structure_id: number;
            name: string;
            period: 'hours' | 'days' | 'weeks' | 'months' | 'years';
            length: number;
            multiplier: number | string;
            quantity: number;
            uuid: string;
            created_at: string;
            updated_at: string;
        }>;
    } | null;
}


export type ListingFilter = {
    year: {
        selected: boolean,
        years: number[],
        available_years: number[],
    },
    make: {
        selected: boolean,
        makes: string[],
        available_makes: string[],
    },
    model: {
        selected: boolean,
        models: string[],
        available_models: string[],
    },
    price_type: {
        selected: boolean,
        price_types: string[],
        available_price_types: string[],
    },
    branch: {
        selected: boolean,
        branch_uuids: string[],
        available_branches: Array<{ uuid: string, name: string }>,
    },
    vehicle_status: {
        selected: boolean,
        statuses: string[],
        available_statuses: string[],
    },
    has_description: {
        selected: boolean,
        value: boolean | null, // true = has description, false = no description, null = any
    },
    has_images: {
        selected: boolean,
        value: boolean | null, // true = has images, false = no images, null = any
    },
}

export type ListingPricingPriceType = 'flat_fee' | 'structure' | 'fixed' | 'none';
export type ListingPricingPeriod = 'Hour' | 'Day' | 'Week' | 'Month' | 'Year';

export type ListingPricing = {
    adjusted_amount: number,
    amount: number,
    base_price_in_cents: number | null,
    discountable: boolean,
    taxable: boolean,
    original_price: number,
    price_period: ListingPricingPeriod,
    /** Card-safe headline price: cheapest bookable tile when a non-linear pricing template applies. */
    display_price?: number,
    display_period?: Nullable<string>,
    is_from?: boolean,
    /** Pricing template name; only present on the fleet-resource listings payload. */
    price_structure_name?: Nullable<string>,
    price_structure: Nullable<string>,
    price_structure_id: Nullable<string>,
    price_type: ListingPricingPriceType,
    sec_dep: number | null,
    security_deposit_credit: number | null,
    security_deposit_debit: number | null,
    total_per_day: number,
    monthlyPercentageDiscount: number | null,
    threeDayPercentageDiscount: number | null,
    weeklyPercentageDiscount: number | null,
}

export enum ListingLoadingSubject {
    Listing = 'listing',
    Listings = 'listings',
    AddListing = 'addListing',
    SaveListing = 'saveListing',
    DeleteListing = 'deleteListing',
    ArchiveListing = 'archiveListing',
    Pausing = 'pausing',
    DecodingVin = 'decoding_vin',
    UnavailablePeriod = 'unavailable_period',
    MatchingTeslaVehicle = 'matchingTeslaVehicle',
    MatchBouncieVehicle = 'matchBouncieVehicle',
    TripExtraAdd = 'tripExtraAdd',
    TripExtraEdit = 'tripExtraEdit',
    ImportListings = 'import_listings',
    ImportImages = 'importImages',
    ImportDelete = 'importDelete',
    SyncCarShareData = 'syncCarShareData',
    DailyPrices = 'dailyPrices',
    UpdateDailyPrices = 'updateDailyPrices',
    UpdateOdomProvider = 'updateOdomProvider',
    Vehicle = 'vehicle',
    VehicleDetails = 'vehicleDetails',
    GetIncidents = 'getIncidents',
    SaveIncident = 'saveIncident',
    DeleteIncident = 'deleteIncident',
    UpdateIncident = 'updateIncident',
    UploadIncidentPhotos = 'uploadIncidentPhotos',
    DeleteIncidentPhoto = 'deleteIncidentPhoto',
    ListingExtras = 'listing_extras',
    VehicleFeatures = 'vehicleFeatures',
}
